import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';

/**
 * Catches render errors from the routed page so one failing component cannot blank
 * the whole application. React unmounts the entire tree on an uncaught render error,
 * which is why a single throw (an expired MUI X licence, for example) takes out the
 * navigation and everything else along with the page.
 *
 * The error clears when the route changes, so navigating away recovers without a
 * reload.
 */
class RouteErrorBoundaryInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, locationKey: props.locationKey };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    static getDerivedStateFromProps(props, state) {
        // A new route gets a clean slate; without this the fallback would persist
        // after navigating away from the page that threw.
        if (props.locationKey !== state.locationKey) {
            return { error: null, locationKey: props.locationKey };
        }
        return null;
    }

    componentDidCatch(error, errorInfo) {
        console.error('[RouteErrorBoundary] Caught a render error:', error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ error: null });
    };

    render() {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        return (
            <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <Alert
                    severity="error"
                    variant="outlined"
                    sx={{ textAlign: 'left', maxWidth: 720 }}
                    action={
                        <Button color="inherit" size="small" onClick={this.handleRetry}>
                            Try again
                        </Button>
                    }
                >
                    <AlertTitle>This page could not be displayed</AlertTitle>
                    <Typography variant="body2">
                        Something went wrong while loading this page. You can try again, or use the
                        menu to go to another page.
                    </Typography>
                    {error.message && (
                        <Typography variant="caption" component="p" sx={{ mt: 1, opacity: 0.8 }}>
                            {error.message}
                        </Typography>
                    )}
                </Alert>
            </Box>
        );
    }
}

RouteErrorBoundaryInner.propTypes = {
    children: PropTypes.node,
    locationKey: PropTypes.string,
};

/**
 * Wraps the routed content. Reads the current path so the boundary can reset itself
 * on navigation.
 */
function RouteErrorBoundary({ children }) {
    const location = useLocation();

    return (
        <RouteErrorBoundaryInner locationKey={location.pathname}>
            {children}
        </RouteErrorBoundaryInner>
    );
}

RouteErrorBoundary.propTypes = {
    children: PropTypes.node,
};

export default RouteErrorBoundary;
